import React from 'react';
import { Award, Users, Target, Heart, Mail, Linkedin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AnimatedText from '../components/AnimatedText';
import FloatingCard from '../components/FloatingCard';
import ShimmerButton from '../components/ShimmerButton';
import { mockTeamMembers } from '../data/mockData';
import { Link } from 'react-router-dom';

const About = () => {
  const values = [
    {
      icon: <Award className="w-8 h-8 text-amber-500" />,
      title: "Excellence",
      description: "Nous visons la perfection dans chaque projet, livrant des solutions qui dépassent les attentes."
    },
    {
      icon: <Users className="w-8 h-8 text-amber-500" />,
      title: "Collaboration",
      description: "Nous travaillons étroitement avec nos clients pour comprendre leur vision et la concrétiser."
    },
    {
      icon: <Target className="w-8 h-8 text-amber-500" />,
      title: "Innovation",
      description: "Nous adoptons les nouvelles technologies et approches créatives pour résoudre les défis complexes."
    },
    {
      icon: <Heart className="w-8 h-8 text-amber-500" />,
      title: "Passion",
      description: "Nous aimons ce que nous faisons et cela se reflète dans la qualité de notre travail et notre dévouement aux clients."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Section Hero */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(251,191,36,0.1),transparent_70%)]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <AnimatedText 
              className="text-4xl sm:text-5xl font-bold text-white mb-6"
              isH1={true}
            >
              À Propos de <span className="title-gradient">Soleil d&apos;Azur</span>
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-300 leading-relaxed font-inter"
              delay={200}
            >
              Nous sommes une équipe passionnée de professionnels créatifs dédiés à aider les marques à briller dans le paysage numérique. Notre parcours a commencé avec une conviction simple : chaque marque a une histoire unique qui mérite d&apos;être racontée.
            </AnimatedText>
          </div>
        </div>
      </section>

      {/* Section Notre Histoire */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <AnimatedText 
                className="text-3xl sm:text-4xl font-inter font-bold text-white"
                isTitle={true}
              >
                Notre Histoire
              </AnimatedText>
              <div className="space-y-4 text-gray-400 leading-relaxed font-inter">
                <AnimatedText delay={200}>
                  <p>
                    Fondée en 2024 sur la magnifique Côte d&apos;Azur française, Soleil d&apos;Azur a émergé d&apos;une passion pour créer des connexions significatives entre les marques et leurs audiences. Notre nom, qui signifie &quot;Soleil de la Côte d&apos;Azur&quot;, reflète notre engagement à apporter chaleur, clarté et brillance à chaque projet.
                  </p>
                </AnimatedText>
                <AnimatedText delay={400}>
                  <p>
                    Ce qui a commencé comme un petit studio créatif est devenu une agence de communication complète, mais nous n&apos;avons jamais perdu de vue nos valeurs fondamentales : authenticité, créativité et partenariat véritable avec nos clients.
                  </p>
                </AnimatedText>
                <AnimatedText delay={600}>
                  <p>
                    Aujourd&apos;hui, nous travaillons avec des entreprises de toutes tailles, des startups innovantes aux entreprises établies, les aidant à naviguer dans le monde complexe de la communication moderne avec confiance et style.
                  </p>
                </AnimatedText>
              </div>
            </div>
            <div className="lg:pl-8">
              <AnimatedText delay={800}>
                <FloatingCard className="bg-gray-800 border border-gray-700" delay={0.5}>
                  <div className="p-8">
                    <div className="space-y-6">
                      <div className="text-center">
                        <AnimatedText 
                          className="text-4xl font-inter font-bold text-white mb-2"
                          isTitle={true}
                          delay={900}
                        >
                          2024
                        </AnimatedText>
                        <AnimatedText 
                          className="text-gray-400 font-inter"
                          delay={1000}
                        >
                          Fondée
                        </AnimatedText>
                      </div>
                      <div className="border-t border-gray-700 pt-6">
                        <div className="grid grid-cols-2 gap-4 text-center">
                          <div>
                            <AnimatedText 
                              className="text-2xl font-inter font-bold text-amber-500 mb-1"
                              isTitle={true}
                              delay={1100}
                            >
                              150+
                            </AnimatedText>
                            <AnimatedText 
                              className="text-sm text-gray-400 font-inter"
                              delay={1200}
                            >
                              Projets
                            </AnimatedText>
                          </div>
                          <div>
                            <AnimatedText 
                              className="text-2xl font-inter font-bold text-amber-500 mb-1"
                              isTitle={true}
                              delay={1300}
                            >
                              50+
                            </AnimatedText>
                            <AnimatedText 
                              className="text-sm text-gray-400 font-inter"
                              delay={1400}
                            >
                              Clients
                            </AnimatedText>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </FloatingCard>
              </AnimatedText>
            </div>
          </div>
        </div>
      </section>

      {/* Section Nos Valeurs */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white mb-4"
              isTitle={true}
            >
              Nos Valeurs
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-400 max-w-3xl mx-auto font-inter"
              delay={200}
            >
              Ces principes fondamentaux guident tout ce que nous faisons et façonnent notre façon de travailler avec nos clients.
            </AnimatedText>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <AnimatedText key={index} delay={300 + index * 100}>
                <FloatingCard className="bg-gray-900 border border-gray-700 hover:border-amber-500/50 transition-all duration-300" delay={index * 0.2}>
                  <CardContent className="p-8 text-center">
                    <div className="mb-6 flex justify-center">{value.icon}</div>
                    <AnimatedText 
                      className="text-xl font-inter font-semibold text-white mb-4"
                      isTitle={true}
                      delay={400 + index * 100}
                    >
                      {value.title}
                    </AnimatedText>
                    <AnimatedText 
                      className="text-gray-400 leading-relaxed font-inter"
                      delay={500 + index * 100}
                    >
                      {value.description}
                    </AnimatedText>
                  </CardContent>
                </FloatingCard>
              </AnimatedText>
            ))}
          </div>
        </div>
      </section>

      {/* Section Rencontrez Notre Équipe */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white mb-4"
              isTitle={true}
            >
              Rencontrez Notre Équipe
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-400 max-w-3xl mx-auto font-inter"
              delay={200}
            >
              Notre équipe talentueuse rassemble une expertise diverse et une passion partagée pour une communication exceptionnelle.
            </AnimatedText>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {mockTeamMembers.map((member, index) => (
              <AnimatedText key={index} delay={300 + index * 200}>
                <FloatingCard className="bg-gray-800 border border-gray-700 overflow-hidden hover:border-amber-500/50 transition-all duration-300" delay={index * 0.3}>
                  <div className="aspect-square bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                    <div className="w-32 h-32 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-3xl font-inter">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-8">
                    <AnimatedText 
                      className="text-2xl font-inter font-semibold text-white mb-2"
                      isTitle={true}
                      delay={400 + index * 200}
                    >
                      {member.name}
                    </AnimatedText>
                    <AnimatedText 
                      className="text-amber-500 font-medium mb-2 font-inter"
                      delay={500 + index * 200}
                    >
                      {member.role}
                    </AnimatedText>
                    <AnimatedText 
                      className="text-gray-400 font-medium mb-4 font-inter"
                      delay={600 + index * 200}
                    >
                      {member.specialties}
                    </AnimatedText>
                    <AnimatedText 
                      className="text-gray-400 text-sm leading-relaxed mb-4 font-inter"
                      delay={700 + index * 200}
                    >
                      {member.bio}
                    </AnimatedText>
                    <AnimatedText delay={800 + index * 200}>
                      <div className="flex space-x-3">
                        <Button size="sm" variant="outline" className="p-2 border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white">
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="p-2 border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white">
                          <Linkedin className="w-4 h-4" />
                        </Button>
                      </div>
                    </AnimatedText>
                  </CardContent>
                </FloatingCard>
              </AnimatedText>
            ))}
          </div>
        </div>
      </section>

      {/* Section Appel à l'Action */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white"
              isTitle={true}
            >
              Prêt à Travailler Avec Nous ?
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-amber-100 font-inter"
              delay={200}
            >
              Discutons de la façon dont nous pouvons aider votre marque à atteindre son plein potentiel. Nous sommes impatients d&apos;apprendre à connaître votre projet et de partager nos idées.
            </AnimatedText>
            <AnimatedText delay={400}>
              <ShimmerButton asChild size="lg" variant="secondary" className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                <Link to="/contact">Contactez-Nous</Link>
              </ShimmerButton>
            </AnimatedText>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
